import React, { useState, useEffect } from 'react';
import { 
  Globe, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Calendar,
  MapPin,
  DollarSign,
  Eye,
  MoreVertical,
  AlertCircle,
  Plane,
  Clock,
  Star,
  Users,
  Shield,
  ArrowRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { travelService } from '../../lib/travelService';
import { Database } from '../../types/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { SearchInput } from '../common/SearchInput';
import { TripForm } from './TripForm';
import { format, isBefore, isAfter, differenceInDays } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

type Trip = Database['public']['Tables']['trips']['Row'];

export function Trips() {
  const { user } = useAuth();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTrip, setEditingTrip] = useState<Trip | null>(null);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    loadTrips();
  }, [user]);

  const loadTrips = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      const tripsData = await travelService.trips.getAll();
      setTrips(tripsData);
    } catch (err) {
      console.error('Error loading trips:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load trips';
      setError(errorMessage);
      
      // Auto-retry logic
      if (retryCount < 3) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          loadTrips();
        }, 2000 * (retryCount + 1));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTrip = async (trip: Trip) => {
    setTrips(prev => [...prev, trip]);
    setShowAddModal(false);
    
    // Navigate to the newly created trip's dashboard
    // We need to navigate to the main travel management with the trip selected
    window.dispatchEvent(new CustomEvent('navigate-to-trip', { detail: { tripId: trip.id } }));
  };

  const handleUpdateTrip = async (trip: Trip) => {
    setTrips(prev => prev.map(t => t.id === trip.id ? trip : t));
    setEditingTrip(null);
  };

  const handleDeleteTrip = async (tripId: string) => {
    if (!confirm('Are you sure you want to delete this trip? This action cannot be undone.')) {
      return;
    }

    try {
      await travelService.trips.delete(tripId);
      setTrips(prev => prev.filter(t => t.id !== tripId));
    } catch (err) {
      console.error('Error deleting trip:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete trip');
    }
  };

  const filteredTrips = trips.filter(trip => {
    const matchesSearch = trip.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (trip.destination && trip.destination.toLowerCase().includes(searchQuery.toLowerCase())) ||
                         (trip.description && trip.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || trip.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planned':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'planned':
        return <Clock size={16} />;
      case 'in_progress':
        return <Plane size={16} />;
      case 'completed':
        return <CheckCircle size={16} />;
      case 'cancelled':
        return <XCircle size={16} />;
      default:
        return <AlertTriangle size={16} />;
    }
  };

  const getTripStatus = (trip: Trip) => {
    const now = new Date();
    const startDate = new Date(trip.start_date);
    const endDate = new Date(trip.end_date);

    if (trip.status === 'cancelled') return 'cancelled';
    if (trip.status === 'completed') return 'completed';
    if (isBefore(now, startDate)) return 'upcoming';
    if (isAfter(now, endDate)) return 'completed';
    return 'in_progress';
  };

  const getDaysUntilTrip = (startDate: string) => {
    const now = new Date();
    const start = new Date(startDate);
    const days = differenceInDays(start, now);
    
    if (days < 0) return 'Past';
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `${days} days`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-mint-50 via-white to-mint-100 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            {/* Header skeleton */}
            <div className="flex items-center justify-between">
              <div>
                <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
              <div className="h-12 bg-gray-200 rounded-lg w-32"></div>
            </div>
            
            {/* Filters skeleton */}
            <div className="flex gap-4">
              <div className="h-12 bg-gray-200 rounded-xl flex-1"></div>
              <div className="h-12 bg-gray-200 rounded-xl w-48"></div>
            </div>
            
            {/* Cards skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="h-80 bg-gray-200 rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-mint-50 via-white to-mint-100 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <AlertCircle className="h-12 w-12 text-red-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Trips</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">{error}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={loadTrips}
                variant="primary"
                className="flex items-center space-x-2"
              >
                <RefreshCw size={16} />
                <span>Try Again</span>
              </Button>
              <Button
                onClick={() => setShowAddModal(true)}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Plus size={16} />
                <span>Add First Trip</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-mint-50 via-white to-mint-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">My Trips</h1>
            <p className="text-xl text-gray-600">Manage your travel plans and itineraries</p>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            variant="primary"
            className="flex items-center space-x-2 px-6 py-3 text-lg"
          >
            <Plus size={20} />
            <span>New Trip</span>
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex-1">
            <SearchInput
              placeholder="Search trips by title, destination, or description..."
              value={searchQuery}
              onChange={setSearchQuery}
              icon={<Search size={18} />}
              className="text-lg"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter size={18} className="text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent text-lg"
            >
              <option value="all">All Status</option>
              <option value="planned">Planned</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Trips Grid */}
        <AnimatePresence>
          {filteredTrips.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
                <div className="text-center py-16">
                  <div className="mx-auto w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-6">
                    <Globe className="h-12 w-12 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {searchQuery || statusFilter !== 'all' ? 'No trips found' : 'No trips yet'}
                  </h3>
                  <p className="text-gray-600 mb-8 max-w-md mx-auto">
                    {searchQuery || statusFilter !== 'all' 
                      ? 'Try adjusting your search or filter criteria'
                      : 'Start planning your next adventure by creating your first trip'
                    }
                  </p>
                  <Button
                    onClick={() => setShowAddModal(true)}
                    variant="primary"
                    className="flex items-center space-x-2 px-8 py-3 text-lg"
                  >
                    <Plus size={20} />
                    <span>Create Your First Trip</span>
                  </Button>
                </div>
              </Card>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTrips.map((trip, index) => (
                <motion.div
                  key={trip.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card 
                    className="h-full cursor-pointer transition-all duration-300 hover:shadow-xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden"
                    onClick={() => {
                      // Navigate to the trip dashboard
                      window.dispatchEvent(new CustomEvent('navigate-to-trip', { detail: { tripId: trip.id } }));
                    }}
                  >
                    {/* Trip Image or Placeholder */}
                    <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 relative">
                      <div className="absolute inset-0 bg-black/20"></div>
                      <div className="absolute top-4 right-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(trip.status)}`}>
                          {getStatusIcon(trip.status)}
                          <span className="ml-1 capitalize">{trip.status.replace('_', ' ')}</span>
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4 text-white">
                        <h3 className="text-xl font-bold mb-1">{trip.title}</h3>
                        {trip.destination && (
                          <p className="flex items-center space-x-1 text-sm opacity-90">
                            <MapPin size={14} />
                            <span>{trip.destination}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Trip Details */}
                    <div className="p-6">
                      <div className="space-y-4">
                        {/* Dates */}
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-2 text-gray-600">
                            <Calendar size={16} />
                            <span>{format(new Date(trip.start_date), 'MMM dd')} - {format(new Date(trip.end_date), 'MMM dd, yyyy')}</span>
                          </div>
                          <span className="text-mint-600 font-medium">
                            {getDaysUntilTrip(trip.start_date)}
                          </span>
                        </div>

                        {/* Description */}
                        {trip.description && (
                          <p className="text-gray-600 text-sm line-clamp-2">{trip.description}</p>
                        )}

                        {/* Budget */}
                        {trip.budget && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <DollarSign size={16} />
                            <span>Budget: {trip.currency} {trip.budget.toLocaleString()}</span>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <div className="flex space-x-2">
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTrip(trip);
                              }}
                              variant="outline"
                              size="sm"
                              className="flex items-center space-x-1"
                            >
                              <Edit size={14} />
                              <span>Edit</span>
                            </Button>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTrip(trip.id);
                              }}
                              variant="outline"
                              size="sm"
                              className="flex items-center space-x-1 text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <Trash2 size={14} />
                              <span>Delete</span>
                            </Button>
                          </div>
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                            <ArrowRight size={16} className="text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>

        {/* Modals */}
        {showAddModal && (
          <TripForm
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            onSuccess={handleCreateTrip}
          />
        )}

        {editingTrip && (
          <TripForm
            isOpen={!!editingTrip}
            onClose={() => setEditingTrip(null)}
            onSuccess={handleUpdateTrip}
            trip={editingTrip}
          />
        )}
      </div>
    </div>
  );
}